import React, { useState, useEffect, useRef } from 'react';
import { auth, db, storage, functions } from '../firebase/firebase';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { signOut, updateProfile } from 'firebase/auth';
import { setupDefaultAvatar, needsDefaultAvatar } from '../services/avatarService';
import CertificateViewerModal from './CertificateViewerModal';
import './ProfileEditModal.css';
import '../components/ProjectDashboardNotif.css';

// Toast notification component - using ProjectDashboard notification styling
const Toast = ({ message, type = 'info', onClose }) => {
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFading(true);
      // Wait for fade-out animation to complete before calling onClose
      setTimeout(() => {
        onClose();
      }, 1000); // Match the fadeOut animation duration
    }, 5000); // Auto-close after 5 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsFading(true);
    setTimeout(() => {
      onClose();
    }, 1000); // Match the fadeOut animation duration
  };

  return (
    <div className="notification-panel">
      <div className={`notification-item ${isFading ? 'fade-out' : ''}`}>
        <div className="notification-header">
          <div className="notification-title">
            Profile Setup
          </div>
          <button
            className="notification-close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>
        <div className="notification-body">
          {message}
        </div>

      </div>
    </div>
  );
};

export default function ProfileEditModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showProfilePicToast, setShowProfilePicToast] = useState(false);
  
  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  
  // File upload
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Certifications
  const [certificationUrls, setCertificationUrls] = useState([]);
  const [uploadingCerts, setUploadingCerts] = useState(false);
  const certFileInputRef = useRef(null);
  const [certViewer, setCertViewer] = useState({ open: false, url: null });
  const [certToDelete, setCertToDelete] = useState(null);

  // Account deletion
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCompanyId, setUserCompanyId] = useState(null);

  useEffect(() => {
    if (isOpen && auth.currentUser) {
      fetchUserProfile();
    }
  }, [isOpen]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFirstName(userData.firstName || '');
        setLastName(userData.lastName || '');
        setEmail(auth.currentUser.email || '');
        setPhoneNumber(userData.phoneNumber || userData.phone || '');
        setUserRole(userData.role || null);
        setUserCompanyId(userData.companyId || null);
        
        // Fetch profile picture from global_backup (single source of truth)
        let picUrl = '/crewchainDefault.png';
        
        try {
          const globalProfileDoc = await getDoc(doc(db, `global_backup/users/profiles/${auth.currentUser.uid}`));
          if (globalProfileDoc.exists()) {
            const globalData = globalProfileDoc.data();
            picUrl = globalData?.profilePictureUrl || '/crewchainDefault.png';
            setCertificationUrls(globalData?.certificationUrls || userData.certificationUrls || []);
          } else {
            setCertificationUrls(userData.certificationUrls || []);
          }
        } catch (error) {
          console.log('Could not fetch profile from global_backup, using fallback:', error);
          // Fallback to userData if global_backup fetch fails
          if (userData.profilePictureUrl && userData.profilePictureUrl !== '/crewchainDefault.png') {
            picUrl = userData.profilePictureUrl;
          } else if (userData.photoURL && userData.photoURL !== '/crewchainDefault.png') {
            picUrl = userData.photoURL;
          }
        }
        
        setProfilePictureUrl(picUrl);
        
        // Only set up default avatar if user truly has no picture at all
        if (needsDefaultAvatar(userData)) {
          try {
            await setupDefaultAvatar(auth.currentUser.uid, userData);
          } catch (error) {
            console.error('Error setting up default avatar:', error);
          }
        }
        
        // Check if user has a real custom profile picture (not default)
        const hasRealProfilePic = (userData.profilePictureUrl && 
                                   userData.profilePictureUrl !== '/crewchainDefault.png') ||
                                  (userData.photoURL && 
                                   userData.photoURL !== '/crewchainDefault.png');
        
        // Only show toast if user has no custom picture
        if (!hasRealProfilePic) {
          setShowProfilePicToast(true);
        }
      } else {
        // Create default user document if it doesn't exist
        setFirstName('');
        setLastName('');
        setEmail(auth.currentUser.email || '');
        setPhoneNumber('');
        setProfilePictureUrl('/crewchainDefault.png');
        
        // Set up default avatar for new users
        try {
          await setupDefaultAvatar(auth.currentUser.uid, {});
          console.log('✅ Default CrewChain avatar set for new user');
        } catch (error) {
          console.error('Error setting up default avatar for new user:', error);
        }
        
        // Show toast for new users
        setShowProfilePicToast(true);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Upload to Firebase Storage (global backup path for consistency)
      const storageRef = ref(storage, `global_backup/users/${auth.currentUser.uid}/profile.jpg`);
      
      // Set correct metadata for images
      const metadata = {
        contentType: 'image/jpeg'
      };
      
      await uploadBytes(storageRef, file, metadata);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      setProfilePictureUrl(downloadURL);
      
      // Hide the profile picture toast since user now has a picture
      setShowProfilePicToast(false);
      
      setSuccess('Profile picture updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const getStoragePathFromUrl = (url) => {
    try {
      const u = new URL(url);
      const match = u.pathname.match(/\/o\/(.+)/);
      if (!match) return null;
      return decodeURIComponent(match[1].split('?')[0]);
    } catch { return null; }
  };

  const getCertFilename = (url) => {
    try {
      const path = getStoragePathFromUrl(url);
      const rawName = path?.split('/').pop() || 'Certification';
      return rawName.replace(/^\d+_/, '') || rawName;
    } catch { return 'Certification'; }
  };

  const handleCertUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setUploadingCerts(true);
      setError(null);
      const uid = auth.currentUser.uid;
      const newUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 20 * 1024 * 1024) {
          setError(`${file.name} exceeds the 20MB limit`);
          continue;
        }
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const certPath = `global_backup/users/${uid}/certs/${timestamp}_${safeName}`;
        const storageRef = ref(storage, certPath);
        await uploadBytes(storageRef, file);

        const ext = file.name.split('.').pop().toLowerCase();
        const isOffice = ext === 'doc' || ext === 'docx';

        if (isOffice) {
          const convertCertToPdf = httpsCallable(functions, 'convertCertToPdf');
          const result = await convertCertToPdf({ storagePath: certPath });
          newUrls.push(result.data.pdfUrl);
        } else {
          const downloadUrl = await getDownloadURL(storageRef);
          newUrls.push(downloadUrl);
        }
      }

      if (newUrls.length === 0) return;

      const globalRef = doc(db, 'global_backup/users/profiles', uid);
      await updateDoc(doc(db, 'users', uid), {
        certificationUrls: arrayUnion(...newUrls)
      });
      await updateDoc(globalRef, {
        certificationUrls: arrayUnion(...newUrls)
      }).catch(async (err) => {
        if (err.code === 'not-found') {
          await setDoc(globalRef, { certificationUrls: newUrls }, { merge: true });
        }
      });

      setCertificationUrls(prev => [...prev, ...newUrls]);
      setSuccess('Certification(s) uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading certification:', err);
      setError('Failed to upload certification: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingCerts(false);
      e.target.value = '';
    }
  };

  const handleDeleteCert = async (url) => {
    try {
      const uid = auth.currentUser.uid;
      const path = getStoragePathFromUrl(url);
      if (path) {
        await deleteObject(ref(storage, path)).catch(() => {});
      }
      await updateDoc(doc(db, 'users', uid), {
        certificationUrls: arrayRemove(url)
      });
      await updateDoc(doc(db, 'global_backup/users/profiles', uid), {
        certificationUrls: arrayRemove(url)
      }).catch(() => {});
      setCertificationUrls(prev => prev.filter(u => u !== url));
    } catch (err) {
      console.error('Error deleting certification:', err);
      setError('Failed to remove certification');
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Update user document
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        profilePictureUrl: profilePictureUrl,
        updatedAt: new Date()
      });

      // Keep Firebase Auth's displayName in sync too. Firestore is the actual source of
      // truth the app reads from, so a failure here shouldn't block the save.
      try {
        await updateProfile(auth.currentUser, {
          displayName: `${firstName.trim()} ${lastName.trim()}`
        });
      } catch (error) {
        console.error('Error updating Auth displayName:', error);
      }

      // Also update global_backup (single source of truth for profile pictures)
      try {
        const globalBackupRef = doc(db, 'global_backup/users/profiles', auth.currentUser.uid);
        await updateDoc(globalBackupRef, {
          profilePictureUrl: profilePictureUrl,
          lastUpdated: new Date()
        }).catch(async (error) => {
          // If document doesn't exist, create it with setDoc
          if (error.code === 'not-found') {
            await setDoc(globalBackupRef, {
              profilePictureUrl: profilePictureUrl,
              lastUpdated: new Date()
            }, { merge: true });
          } else {
            throw error;
          }
        });
      } catch (error) {
        console.error('Error updating global_backup:', error);
        // Don't fail the whole operation if global_backup update fails
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = () => {
    setProfilePictureUrl('');
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      setError(null);

      // Call Firebase Cloud Function to delete account
      const deleteMyAccount = httpsCallable(functions, 'deleteMyAccount');
      const result = await deleteMyAccount({
        reason: deletionReason.trim() || 'User requested self-deletion'
      });

      const resultData = result.data;
      
      if (resultData && resultData.success) {
        // Check if Auth deletion succeeded
        const authDeleted = resultData.authDeleted !== false; // Default to true if not specified (backward compatibility)
        const authDeletionError = resultData.authDeletionError;
        
        if (!authDeleted) {
          console.error('⚠️ Account deletion completed but Firebase Auth deletion failed:', authDeletionError);
          // Still proceed with sign out and redirect, but log the issue
          // The user's Firestore data is deleted, so they can't sign in anyway
        }
        
        // Sign out from Firebase Auth after successful deletion
        try {
          await signOut(auth);
          console.log('✅ User signed out from Firebase Auth');
        } catch (signOutError) {
          console.warn('⚠️ Error signing out from Firebase Auth:', signOutError);
          // Don't fail the deletion if sign out fails - the user is already deleted
        }

        // Close modal
        onClose();

        // Clear any stored data
        localStorage.clear();
        sessionStorage.clear();
        
        // Force page reload to clear any cached data and redirect to login
        // The signOut() above will trigger AuthContext to set user=null, 
        // and ProtectedRoute will redirect, but we force it here for immediate redirect
        window.location.href = '/';
      } else {
        const errorMessage = resultData?.message || 'Unknown error occurred';
        setError(`Failed to delete account: ${errorMessage}`);
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('❌ Error deleting account:', error);
      setError(`Failed to delete account: ${error.message || 'Unknown error'}`);
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay">
      {/* Profile Picture Toast */}
      {showProfilePicToast && (
        <Toast
          message="Let other collaborators recognise you by adding a profile picture!"
          type="info"
          onClose={() => setShowProfilePicToast(false)}
        />
      )}
      
      <div className="profile-modal-content">
        <div className="profile-modal-header">
          <h2>Edit Profile</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        ) : (
          <div className="profile-modal-body">
            {/* Profile Picture Section */}
            <div className="profile-picture-section">
              <div className="profile-picture-container">
                <img
                  src={profilePictureUrl || '/crewchainDefault.png'}
                  alt="Profile"
                  className="profile-picture"
                />
                <div className="profile-picture-overlay">
                  <button
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Change Photo'}
                  </button>
                  {profilePictureUrl && (
                    <button
                      className="remove-btn"
                      onClick={handleRemoveImage}
                      disabled={uploadingImage}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <p className="upload-hint">
                JPG, PNG or GIF. Max 5MB.
              </p>
            </div>

            {/* Form Fields */}
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                className="form-input disabled"
                title="Email cannot be changed"
              />
              <small className="field-hint">
                Email address cannot be modified. Contact support if you need to change it.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. +44 7700 900000"
                className="form-input"
              />
              <small className="field-hint">
                Your contact number for emergency roll call and team communication.
              </small>
            </div>

            {/* Certifications Section */}
            <div className="certifications-section">
              <div className="certifications-header">
                <h3 className="certifications-title">Certifications</h3>
                <button
                  className="btn-add-cert"
                  onClick={() => certFileInputRef.current?.click()}
                  disabled={uploadingCerts}
                >
                  {uploadingCerts ? 'Uploading...' : '+ Add'}
                </button>
              </div>
              <input
                ref={certFileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                multiple
                onChange={handleCertUpload}
                style={{ display: 'none' }}
              />
              {certificationUrls.length > 0 ? (
                <div className="cert-grid">
                  {certificationUrls.map((url) => {
                    const isPdf = url.toLowerCase().includes('.pdf');
                    const filename = getCertFilename(url);
                    return (
                      <div
                        key={url}
                        className="cert-tile"
                        onClick={() => setCertViewer({ open: true, url })}
                        title={`View ${filename}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <button
                          className="cert-delete-btn"
                          onClick={(e) => { e.stopPropagation(); setCertToDelete(url); }}
                          title="Remove certification"
                        >×</button>
                        <div className={`cert-icon ${isPdf ? 'cert-icon-pdf' : 'cert-icon-image'}`}>
                          {isPdf ? (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                            </svg>
                          )}
                        </div>
                        <span className="cert-filename" title={filename}>{filename}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="cert-empty">No certifications uploaded yet.</p>
              )}
              <p className="cert-hint">PDF, image, or Word document. Max 20MB per file.</p>
            </div>

            {/* Error and Success Messages */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            {/* Account Deletion Section */}
            <div className="account-deletion-section" style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid var(--branding-app-surface-panel-border)'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#dc2626',
                marginBottom: '8px'
              }}>
                Delete Account
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--cc-text-muted)',
                marginBottom: '16px',
                lineHeight: '1.5'
              }}>
                Permanently delete your account and anonymise your data. Historical project records will be preserved for compliance purposes. This action cannot be undone.
              </p>
              <button
                className="btn-danger"
                onClick={() => setShowDeleteDialog(true)}
                disabled={saving || isDeleting}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: saving || isDeleting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: saving || isDeleting ? 0.6 : 1
                }}
              >
                Delete My Account
              </button>
            </div>

            {/* Action Buttons */}
            <div className="profile-modal-actions">
              <button
                className="btn-secondary"
                onClick={onClose}
                disabled={saving || isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || isDeleting || !firstName.trim() || !lastName.trim()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="profile-modal-overlay" style={{ zIndex: 1001 }}>
          <div className="profile-modal-content" style={{ maxWidth: '500px' }}>
            <div className="profile-modal-header">
              <h2 style={{ color: '#dc2626' }}>Delete My Account</h2>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletionReason('');
                }}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            
            <div className="profile-modal-body">
              <p style={{
                fontSize: '14px',
                color: 'var(--cc-text-primary)',
                marginBottom: '16px',
                lineHeight: '1.5'
              }}>
                This will permanently delete your account and anonymise your data. This action cannot be undone.
              </p>
              
              <p style={{
                fontSize: '12px',
                color: 'var(--cc-text-muted)',
                marginBottom: userRole === 'pm' && userCompanyId ? '12px' : '20px',
                lineHeight: '1.5'
              }}>
                Historical project records will be preserved for compliance purposes.
              </p>

              {userRole === 'pm' && userCompanyId && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#fefce8',
                  border: '1px solid #fde047',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: '#854d0e',
                  lineHeight: '1.5'
                }}>
                  You are a project manager in a company. If you own any projects, you must transfer them to another PM before deleting your account. Your collaborator access to other projects will be removed automatically.
                </div>
              )}

              <div className="form-group">
                <label htmlFor="deletionReason" style={{ fontSize: '14px', fontWeight: '500' }}>
                  Reason for deletion (optional)
                </label>
                <textarea
                  id="deletionReason"
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  placeholder="Help us improve by sharing why you're leaving..."
                  className="form-input"
                  style={{
                    minHeight: '80px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  disabled={isDeleting}
                />
              </div>

              {error && (
                <div className="error-message" style={{ marginBottom: '16px' }}>
                  {error}
                </div>
              )}

              <div className="profile-modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeletionReason('');
                    setError(null);
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isDeleting ? 0.6 : 1
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {certToDelete && (
        <div className="profile-modal-overlay" style={{ zIndex: 1001 }}>
          <div className="profile-modal-content" style={{ maxWidth: '420px' }}>
            <div className="profile-modal-header">
              <h2 style={{ color: '#dc2626' }}>Remove Certification</h2>
              <button className="close-button" onClick={() => setCertToDelete(null)}>×</button>
            </div>
            <div className="profile-modal-body">
              <p style={{ fontSize: '14px', color: 'var(--cc-text-primary)', marginBottom: '8px', lineHeight: '1.5' }}>
                Are you sure you want to remove <strong>{getCertFilename(certToDelete)}</strong>?
              </p>
              <p style={{ fontSize: '13px', color: 'var(--cc-text-muted)', marginBottom: '24px', lineHeight: '1.5' }}>
                This cannot be undone.
              </p>
              <div className="profile-modal-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                <button className="btn-secondary" onClick={() => setCertToDelete(null)}>Cancel</button>
                <button
                  className="btn-danger"
                  onClick={() => { handleDeleteCert(certToDelete); setCertToDelete(null); }}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CertificateViewerModal
        open={certViewer.open}
        onClose={() => setCertViewer({ open: false, url: null })}
        certificateUrl={certViewer.url}
      />
    </div>
  );
}
